import { Popper, styled, useForkRef } from '@mui/material';
import * as React from 'react';
import { type Components, type ItemProps, type ListProps, Virtuoso } from 'react-virtuoso';

import { VirtualizeProps } from './types';

const LISTBOX_PADDING = 8; // px
const ITEM_HEIGHT = 48; // px
const GROUP_LABEL_HEIGHT = 24; // px
const MAX_ITEMS_COUNT = 8;
// Rows mounted past each edge: MUI only highlights and scrolls to an option that is in the DOM,
// and Page Up/Down move it five rows.
const OVERSCAN_COUNT = 5;

type ListboxItem = React.ReactElement | number | string;

const ListboxList = React.forwardRef<HTMLUListElement, ListProps<HTMLUListElement>>(function ListboxList(
    { children, style, 'data-testid': testId },
    ref,
) {
    return (
        <ul ref={ref} style={style} data-testid={testId}>
            {children}
        </ul>
    );
});

// Virtuoso measures each row by the props it gives the row, so they go on MUI's own `li`
// rather than on a wrapper `div` around it.
function ListboxRow({
    children,
    item: _item,
    context: _context,
    style,
    ...rowProps
}: ItemProps<unknown> & { context?: unknown }) {
    const row = children as React.ReactElement<React.HTMLAttributes<HTMLElement>>;
    return React.cloneElement(row, { ...rowProps, style: { ...row.props.style, ...style } });
}

const ListboxPadding = () => <div style={{ height: LISTBOX_PADDING }} />;

const components: Components<unknown, unknown, HTMLUListElement> = {
    List: ListboxList,
    Item: ListboxRow,
    Header: ListboxPadding,
    Footer: ListboxPadding,
};

const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
    function ListboxComponent(props, ref) {
        // MUI looks up options inside the element that carries its listbox props and ref, and
        // scrolls that element to the highlighted one, so all of them go on Virtuoso's scroller.
        const { children, style, ...listboxProps } = props;
        // Stable, so Virtuoso calls MUI's ref once: MUI resets the highlight on every call.
        const scrollerRef = useForkRef<HTMLElement | Window>(ref);
        const items: ListboxItem[] = [];
        const groupLabelIndices = new Set<number>();

        React.Children.forEach(
            children as ListboxItem[],
            (item: ListboxItem & { props?: VirualizedGroupContainerProps | unknown }) => {
                if (
                    typeof item === 'object' &&
                    item !== null &&
                    'props' in item &&
                    item.props &&
                    typeof item.props === 'object' &&
                    'label' in item.props &&
                    'innerChildren' in item.props
                ) {
                    groupLabelIndices.add(items.length);
                    items.push(item.props.label as ListboxItem);
                    items.push(...((item.props.innerChildren as ListboxItem[]) || []));
                } else {
                    items.push(item);
                }
            },
        );

        const itemCount = items.length;
        const getItemSize = (index: number) => (groupLabelIndices.has(index) ? GROUP_LABEL_HEIGHT : ITEM_HEIGHT);
        const totalHeight = Array.from({ length: itemCount }, (_, i) => getItemSize(i)).reduce((a, b) => a + b, 0);
        const maxHeight = MAX_ITEMS_COUNT * ITEM_HEIGHT;
        const innerHeight = Math.min(totalHeight, maxHeight);

        const renderRow = (index: number) => {
            const item = items[index];
            if (groupLabelIndices.has(index)) {
                return (
                    <li
                        style={{
                            height: GROUP_LABEL_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            boxSizing: 'border-box',
                        }}
                    >
                        {item}
                    </li>
                );
            }
            const option = item as React.ReactElement<React.HTMLAttributes<HTMLElement>>;
            return React.cloneElement(option, { style: { ...option.props.style, height: ITEM_HEIGHT } });
        };

        return (
            <Virtuoso
                {...listboxProps}
                // MUI keeps focus in the input; Virtuoso would make the listbox a tab stop.
                tabIndex={-1}
                scrollerRef={scrollerRef ?? undefined}
                style={{ ...style, height: innerHeight + 2 * LISTBOX_PADDING }}
                totalCount={itemCount}
                itemContent={renderRow}
                components={components}
                defaultItemHeight={ITEM_HEIGHT}
                increaseViewportBy={OVERSCAN_COUNT * ITEM_HEIGHT}
                // Mounts the first rows on the first render, before Virtuoso has measured the
                // viewport, so MUI's auto-highlight finds the first option when the list opens.
                initialItemCount={Math.min(itemCount, MAX_ITEMS_COUNT + OVERSCAN_COUNT)}
            />
        );
    },
);

const StyledPopper = styled(Popper)({
    [`& .MuiAutocomplete-listbox`]: {
        boxSizing: 'border-box',
        '& ul': {
            padding: 0,
            margin: 0,
        },
    },
});

export function generateVirtualizeProps(): VirtualizeProps {
    return {
        listbox: ListboxComponent,
        popper: StyledPopper,
    };
}

type VirualizedGroupContainerProps = {
    label: React.ReactNode;
    innerChildren: React.ReactNode;
};

/** Only for `renderGroup` of a virtualized FieldMultiSelect: the listbox reads its props to flatten the groups. */
export const VirualizedGroupContainer: React.FunctionComponent<VirualizedGroupContainerProps> = (): null => {
    // HACK: renders nothing; the listbox takes its props to count the rows without walking nested children.
    return null;
};
